import {
  checkRateLimit,
  consumeQuestion,
  getUserUsageStats,
  FREE_QUESTIONS_PER_CATALOG,
  DURATION_MINUTES,
} from './rateLimiter';

// Re-export constants for use in components
export { FREE_QUESTIONS_PER_CATALOG, DURATION_MINUTES };

export interface QuestionUsageResult {
  allowed: boolean;
  remainingQuestions: number;
  resetDate?: Date;
  error?: string;
  isPro: boolean;
}

/**
 * Check if user can practice questions with rate limiting and subscription checks
 */
export async function canPracticeQuestions(
  userId: string,
  hasProAccess: boolean,
  catalogId: string
): Promise<QuestionUsageResult> {
  // For free users, rate limiting is per catalog per 30 days
  // For pro users, unlimited access
  const result = await checkRateLimit(userId, hasProAccess, catalogId);

  return {
    allowed: result.allowed,
    remainingQuestions: result.remainingQuestions,
    resetDate: result.resetDate,
    error: result.error,
    isPro: hasProAccess,
  };
}

/**
 * Record a question practice session
 */
export async function recordQuestionPractice(
  userId: string,
  hasProAccess: boolean,
  catalogId: string
): Promise<boolean> {
  return await consumeQuestion(userId, hasProAccess, catalogId);
}

/**
 * Get usage statistics for a user
 */
export async function getQuestionUsageStats(
  userId: string,
  hasProAccess: boolean,
  catalogId: string
): Promise<{
  questionsUsed: number;
  questionsRemaining: number;
  resetDate?: Date;
  isPro: boolean;
}> {
  return await getUserUsageStats(userId, hasProAccess, catalogId);
}

/**
 * Get user subscription status display information
 */
export function getSubscriptionDisplayInfo(hasProAccess: boolean) {
  return {
    plan: hasProAccess ? 'Pro' : 'Free',
    questionsLimit: hasProAccess ? 'Unlimited' : `${10} per catalog`,
    features: hasProAccess
      ? [
          'Unlimited questions per catalog',
          'All practice modes',
          'Priority support',
        ]
      : [
          '10 questions per catalog',
          'Sequential and random modes',
          'Basic support',
        ],
  };
}
